import { ReactNode, useEffect, useRef, useState } from 'react';

export interface DropdownItem {
    key: string;
    label?: ReactNode;
    icon?: ReactNode;
    danger?: boolean;
    disabled?: boolean;
    onClick?: () => void;
    divider?: boolean;
}

/**
 * Menu déroulant — remplace antd Dropdown/Menu et MUI Menu.
 *
 * <Dropdown items={[{ key: 'a', label: 'Action', onClick: fn }]}>
 *   <button>Trigger</button>
 * </Dropdown>
 */
const Dropdown = (props: {
    items: DropdownItem[];
    children: ReactNode;
    placement?: 'bottomLeft' | 'bottomRight';
    className?: string;
}) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const onDocClick = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, []);

    return (
        <div className={`relative inline-block ${props.className || ''}`} ref={ref}>
            <div onClick={() => setOpen(!open)} className="cursor-pointer">
                {props.children}
            </div>
            {open && (
                <div className={`absolute z-50 mt-2 min-w-[220px] py-1.5 bg-[var(--eb-surface)] border border-[var(--eb-border)] rounded-xl shadow-lg
                    ${props.placement === 'bottomRight' ? 'right-0' : 'left-0'}
                    animate-[eb-modal-in_.15s_ease]`}>
                    {props.items.map(item => item.divider ? (
                        <div key={item.key} className="my-1 border-t border-[var(--eb-border)]" />
                    ) : (
                        <button
                            key={item.key}
                            disabled={item.disabled}
                            onClick={() => { item.onClick?.(); if (!item.disabled) setOpen(false); }}
                            className={`w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-left transition-colors
                                ${item.disabled ? 'cursor-default opacity-100' : 'cursor-pointer'}
                                ${item.danger ? 'text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20' : 'text-[var(--eb-text)] hover:bg-[var(--eb-bg)]'}`}
                        >
                            {item.icon && <span className="flex-shrink-0">{item.icon}</span>}
                            <span className="flex-1 min-w-0">{item.label}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Dropdown;
